/* =============================================================================
 * Mapped pre-order shared utilities (PDP block + collection grid)
 * -----------------------------------------------------------------------------
 * Pure lookup, ship-date interpolation, line item properties, and cart add.
 * Used by mapped-preorder-product.js and mapped-preorder-collection.js.
 * ========================================================================== */

(function () {
  'use strict';

  if (window.__sscMappedPreorderCoreLoaded) return;
  window.__sscMappedPreorderCoreLoaded = true;

  var LOG_PREFIX = '[ssc-mapped-preorder-core]';
  var DEFAULT_CART_ADD_URL = '/cart/add.js';

  function norm(value) {
    if (value === null || value === undefined) return '*';
    var s = String(value);
    if (s.length === 0) return '*';
    var trimmed = s.toLowerCase().trim();
    return trimmed.length === 0 ? '*' : trimmed;
  }

  function indexOfOption(optionsWithValues, name) {
    if (!Array.isArray(optionsWithValues)) return -1;
    if (name === null || name === undefined) return -1;
    var target = String(name).toLowerCase().trim();
    if (target.length === 0) return -1;
    for (var i = 0; i < optionsWithValues.length; i++) {
      var opt = optionsWithValues[i];
      if (!opt || typeof opt.name !== 'string') continue;
      if (opt.name.toLowerCase().trim() === target) return i;
    }
    return -1;
  }

  function buildLookup(
    mappedVariants,
    mappedOptionsWithValues,
    mappedColorName,
    mappedSizeName,
  ) {
    var colorIdx = indexOfOption(mappedOptionsWithValues, mappedColorName);
    var sizeIdx = indexOfOption(mappedOptionsWithValues, mappedSizeName);
    var table = Object.create(null);

    if (!Array.isArray(mappedVariants)) {
      return { table: table, colorIdx: colorIdx, sizeIdx: sizeIdx };
    }

    for (var i = 0; i < mappedVariants.length; i++) {
      var v = mappedVariants[i];
      if (!v) continue;
      var opts = Array.isArray(v.options) ? v.options : [];
      var color = colorIdx >= 0 ? norm(opts[colorIdx]) : '*';
      var size = sizeIdx >= 0 ? norm(opts[sizeIdx]) : '*';
      var key = color + '__' + size;

      if (Object.prototype.hasOwnProperty.call(table, key)) {
        console.warn(
          LOG_PREFIX,
          'duplicate key',
          key,
          'previous variant id',
          table[key].id,
          'new variant id',
          v.id,
          '(last wins)',
        );
      }
      table[key] = v;
    }

    return { table: table, colorIdx: colorIdx, sizeIdx: sizeIdx };
  }

  function lookupMappedByColorSize(color, size, lookup) {
    if (!lookup || !lookup.table) return null;

    var c = color === '*' ? '*' : norm(color);
    var s = size === '*' ? '*' : norm(size);
    var candidates = [c + '__' + s, c + '__*', '*__' + s, '*__*'];

    for (var i = 0; i < candidates.length; i++) {
      var hit = lookup.table[candidates[i]];
      if (hit !== undefined) return hit;
    }
    return null;
  }

  /**
   * Strict mapped lookup for PDP size slash / isSizePurchasable only.
   * Exact color+size first; axis wildcards only when that axis is missing on
   * the mapped product. Never *__<size> or *__* when both colorIdx and sizeIdx
   * are valid (avoids attributing another color's stock).
   *
   * @param {string|null|undefined} color
   * @param {string|null|undefined} size
   * @param {object} lookup - output of buildLookup()
   * @returns {object|null}
   */
  function lookupMappedByColorSizeForAvailability(color, size, lookup) {
    if (!lookup || !lookup.table) return null;

    var c = color === '*' ? '*' : norm(color);
    var s = size === '*' ? '*' : norm(size);
    var table = lookup.table;
    var exactKey = c + '__' + s;

    if (Object.prototype.hasOwnProperty.call(table, exactKey)) {
      return table[exactKey];
    }

    var colorIdx = lookup.colorIdx;
    var sizeIdx = lookup.sizeIdx;

    if (colorIdx >= 0 && sizeIdx >= 0) {
      return null;
    }

    if (colorIdx >= 0 && sizeIdx < 0) {
      var colorAxisKey = c + '__*';
      if (Object.prototype.hasOwnProperty.call(table, colorAxisKey)) {
        return table[colorAxisKey];
      }
      return null;
    }

    if (colorIdx < 0 && sizeIdx >= 0) {
      var sizeAxisKey = '*__' + s;
      if (Object.prototype.hasOwnProperty.call(table, sizeAxisKey)) {
        return table[sizeAxisKey];
      }
      return null;
    }

    if (Object.prototype.hasOwnProperty.call(table, '*__*')) {
      return table['*__*'];
    }
    return null;
  }

  function resolveMapped(
    mainVariant,
    mainOptionsWithValues,
    mainColorName,
    mainSizeName,
    lookup,
  ) {
    if (!mainVariant || !lookup) return null;

    var mainColorIdx = indexOfOption(mainOptionsWithValues, mainColorName);
    var mainSizeIdx = indexOfOption(mainOptionsWithValues, mainSizeName);

    var opts = Array.isArray(mainVariant.options) ? mainVariant.options : [];
    var color = mainColorIdx >= 0 ? opts[mainColorIdx] : '*';
    var size = mainSizeIdx >= 0 ? opts[mainSizeIdx] : '*';

    return lookupMappedByColorSize(color, size, lookup);
  }

  /**
   * Resolve a mapped variant from picker color/size values (no main variant required).
   *
   * @param {string|null|undefined} colorValue - selected color option value
   * @param {string|null|undefined} sizeValue - selected size option value
   * @param {object} lookup - output of buildLookup()
   * @returns {object|null}
   */
  function resolveMappedFromSelection(colorValue, sizeValue, lookup) {
    if (!lookup) return null;
    return lookupMappedByColorSize(colorValue, sizeValue, lookup);
  }

  function parseJsonScript(root, selector) {
    if (!root || !root.querySelector) return null;
    var el = root.querySelector(selector);
    if (!el) return null;
    var text = (el.textContent || '').trim();
    if (!text) return null;
    try {
      return JSON.parse(text);
    } catch (e) {
      console.error(LOG_PREFIX, 'failed to parse', selector, e);
      return null;
    }
  }

  function normalizeDateMap(raw) {
    var out = { productDefault: '', byVariantId: Object.create(null) };
    if (!raw || typeof raw !== 'object') return out;
    if (raw.productDefault != null)
      out.productDefault = String(raw.productDefault);
    if (raw.byVariantId && typeof raw.byVariantId === 'object') {
      for (var k in raw.byVariantId) {
        if (Object.prototype.hasOwnProperty.call(raw.byVariantId, k)) {
          var v = raw.byVariantId[k];
          out.byVariantId[String(k)] = v == null ? '' : String(v);
        }
      }
    }
    return out;
  }

  function parseDateMap(root, selector) {
    var raw = parseJsonScript(root, selector);
    return normalizeDateMap(raw);
  }

  function resolveShipDate(dateMap, variant) {
    if (!dateMap) return '';
    if (variant && variant.id != null) {
      var byId = dateMap.byVariantId[String(variant.id)];
      if (typeof byId === 'string' && byId.length > 0) return byId;
    }
    return dateMap.productDefault || '';
  }

  function interpolateShipText(template, dateString) {
    var tpl = template == null ? '' : String(template);
    if (tpl.length === 0) return '';
    if (tpl.indexOf('{date}') === -1) return tpl;
    if (typeof dateString === 'string' && dateString.length > 0) {
      return tpl.split('{date}').join(dateString);
    }
    return tpl.split('{date}').join('').replace(/\s+/g, ' ').trim();
  }

  function formatMoney(cents) {
    try {
      if (
        window.theme &&
        window.theme.Currency &&
        typeof window.theme.Currency.formatMoney === 'function' &&
        window.theme.settings &&
        window.theme.settings.moneyFormat
      ) {
        return window.theme.Currency.formatMoney(
          cents,
          window.theme.settings.moneyFormat,
        );
      }
    } catch (e) {
      /* fall through */
    }
    var n = Number(cents);
    if (!isFinite(n)) return '';
    return '$' + (n / 100).toFixed(2);
  }

  function getCartAddUrl() {
    if (window.theme && window.theme.routes && window.theme.routes.cartAdd) {
      return window.theme.routes.cartAdd;
    }
    console.warn(
      LOG_PREFIX,
      'theme.routes.cartAdd not defined; falling back to',
      DEFAULT_CART_ADD_URL,
    );
    return DEFAULT_CART_ADD_URL;
  }

  function findVariantById(variants, id) {
    if (!Array.isArray(variants) || id == null) return null;
    var target = String(id);
    for (var i = 0; i < variants.length; i++) {
      if (variants[i] && String(variants[i].id) === target) return variants[i];
    }
    return null;
  }

  function indexOfOptionWithColourAlias(optionsWithValues, name) {
    var idx = indexOfOption(optionsWithValues, name);
    if (idx >= 0) return idx;
    if (name === null || name === undefined) return -1;
    if (String(name).toLowerCase().trim() === 'color') {
      return indexOfOption(optionsWithValues, 'Colour');
    }
    return -1;
  }

  /**
   * Find a main-product variant by color + size option values.
   *
   * @param {Array} mainVariants
   * @param {Array} mainOptions - options_with_values
   * @param {string} colorName - configured color option label
   * @param {string} sizeName - configured size option label
   * @param {string|null|undefined} color
   * @param {string|null|undefined} size
   * @returns {object|null}
   */
  function findMainVariantByColorSize(
    mainVariants,
    mainOptions,
    colorName,
    sizeName,
    color,
    size,
  ) {
    if (!Array.isArray(mainVariants) || mainVariants.length === 0) return null;

    var colorIdx = indexOfOptionWithColourAlias(mainOptions, colorName);
    var sizeIdx = indexOfOption(mainOptions, sizeName);
    var colorNorm = norm(color);
    var sizeNorm = norm(size);

    for (var i = 0; i < mainVariants.length; i++) {
      var v = mainVariants[i];
      if (!v) continue;
      var opts = Array.isArray(v.options) ? v.options : [];
      var vColor = colorIdx >= 0 ? norm(opts[colorIdx]) : '*';
      var vSize = sizeIdx >= 0 ? norm(opts[sizeIdx]) : '*';
      if (vColor === colorNorm && vSize === sizeNorm) return v;
    }
    return null;
  }

  /**
   * True when a variant (or product default on the date map) has an active pre-order date.
   *
   * @param {object} dateMap - normalized { productDefault, byVariantId }
   * @param {object|null} variant
   * @returns {boolean}
   */
  function hasPreorderForVariant(dateMap, variant) {
    if (!dateMap) return false;
    if (variant) {
      var ship = resolveShipDate(dateMap, variant);
      return typeof ship === 'string' && ship.length > 0;
    }
    var def = dateMap.productDefault;
    return def != null && String(def).length > 0;
  }

  /**
   * Whether a size is purchasable for the given color. A variant is purchasable
   * only when `variant.available === true` on the main product OR on the mapped
   * product. The `pre_order_date` metafield no longer promotes sold-out
   * variants to purchasable – it only drives date labels / CTA copy elsewhere.
   * Mirrors snippets/variant-size-purchasable.liquid.
   *
   * `ctx.mainDateMap` / `ctx.mappedDateMap` are accepted for signature
   * stability across callers but are not consulted here.
   *
   * @param {string|null|undefined} color
   * @param {string|null|undefined} size
   * @param {object} ctx
   * @param {Array} ctx.mainVariants
   * @param {Array} ctx.mainOptions
   * @param {string} [ctx.mainColorOptionName]
   * @param {string} [ctx.mainSizeOptionName]
   * @param {object} [ctx.mainDateMap]
   * @param {object} [ctx.mappedLookup] - output of buildLookup()
   * @param {object} [ctx.mappedDateMap]
   * @returns {boolean}
   */
  function isSizePurchasable(color, size, ctx) {
    if (!ctx) return true;

    var mainVariants = ctx.mainVariants;
    var mainOptions = ctx.mainOptions;
    var mainColorName =
      ctx.mainColorOptionName || ctx.mainColorOption || 'Color';
    var mainSizeName = ctx.mainSizeOptionName || ctx.mainSizeOption || 'Size';

    var mainVariant = findMainVariantByColorSize(
      mainVariants,
      mainOptions,
      mainColorName,
      mainSizeName,
      color,
      size,
    );

    if (mainVariant && mainVariant.available === true) return true;

    var lookup = ctx.mappedLookup;
    if (lookup) {
      var mappedVariant = lookupMappedByColorSizeForAvailability(
        color,
        size,
        lookup,
      );
      if (mappedVariant && mappedVariant.available === true) return true;
    }

    return false;
  }

  function handleizeOptionName(name) {
    if (name == null) return '';
    return String(name)
      .toLowerCase()
      .trim()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function findSizeFieldset(container, sizeOptionName) {
    if (!container || !container.querySelector) return null;
    var handle = handleizeOptionName(sizeOptionName || 'Size');
    if (handle) {
      var wrap = container.querySelector(
        '.variant-input-wrap[data-handle="' + handle + '"]',
      );
      if (wrap) return wrap;
    }
    return container.querySelector('.variant-input-wrap[data-handle="size"]');
  }

  /**
   * Selected color from PDP pickers (includes mapped-only swatches).
   *
   * @param {Element} container
   * @returns {string|null}
   */
  function readSelectedColorFromContainer(container) {
    if (!container) return null;

    var checked = container.querySelectorAll('[data-variant-input]:checked');
    var regularColor = null;
    var mappedOnlyColor = null;

    for (var i = 0; i < checked.length; i++) {
      var inp = checked[i];
      var isColor =
        (inp.classList && inp.classList.contains('variant__input--color-swatch')) ||
        inp.hasAttribute('data-color-name');
      if (!isColor) continue;

      var val = inp.value || inp.getAttribute('data-color-name') || '';
      if (inp.getAttribute('data-ssc-mapped-only-color') === 'true') {
        mappedOnlyColor = val;
      } else {
        regularColor = val;
      }
    }

    if (mappedOnlyColor) return mappedOnlyColor;
    return regularColor;
  }

  /**
   * Toggle slash styling on size inputs only (never color swatches).
   *
   * @param {Element} container - [data-section-type="product"] root
   * @param {object} ctx - same shape as isSizePurchasable; optional ctx.color
   */
  function updateProductSizePickerAvailability(container, ctx) {
    if (!container || !ctx) return;

    var color =
      ctx.color != null && String(ctx.color).length > 0
        ? ctx.color
        : readSelectedColorFromContainer(container);

    var sizeInputs = container.querySelectorAll('[data-ssc-size-input="true"]');
    if (!sizeInputs.length) {
      var sizeWrap = findSizeFieldset(
        container,
        ctx.mainSizeOptionName || ctx.mainSizeOption || 'Size',
      );
      if (sizeWrap) {
        sizeInputs = sizeWrap.querySelectorAll(
          '[data-variant-input]:not(.variant__input--color-swatch)',
        );
      }
    }

    for (var i = 0; i < sizeInputs.length; i++) {
      var input = sizeInputs[i];
      if (!input || input.nodeType !== 1) continue;
      if (
        (input.classList &&
          input.classList.contains('variant__input--color-swatch')) ||
        input.hasAttribute('data-color-name') ||
        input.getAttribute('data-ssc-mapped-only-color') === 'true'
      ) {
        continue;
      }

      var size = input.value;
      var purchasable = isSizePurchasable(color, size, ctx);
      var variantInput = input.closest('.variant-input');
      var label = variantInput ? variantInput.querySelector('label') : null;

      if (purchasable) {
        input.classList.remove('disabled');
        if (label) label.classList.remove('disabled');
      } else {
        input.classList.add('disabled');
        if (label) label.classList.add('disabled');
      }
    }
  }

  /**
   * Featured image URL from a Shopify variant JSON object (mapped or main).
   * Prefers `featured_media.preview_image.src`, then `featured_image.src`.
   *
   * @param {object|null|undefined} variant
   * @returns {string} empty when no featured image
   */
  function getVariantFeaturedSrc(variant) {
    if (!variant) return '';
    var media = variant.featured_media;
    if (media && media.preview_image && media.preview_image.src) {
      return String(media.preview_image.src);
    }
    var img = variant.featured_image;
    if (img && img.src) return String(img.src);
    return '';
  }

  /**
   * Slug for theme color image sets (`data-group="{setName}_{slug}"`).
   * Matches `theme.Product.getImageSetName` in theme.js.liquid.
   *
   * @param {string|null|undefined} value - e.g. color option label
   * @returns {string}
   */
  function slugifyImageSetName(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/-$/, '')
      .replace(/^-/, '');
  }

  /**
   * Build line item properties for mapped / metafield pre-order adds.
   *
   * @param {object} opts
   * @param {boolean} opts.enableProperties
   * @param {string} opts.purchaseTypeLabel
   * @param {string} opts.shipDateTemplate
   * @param {string} [opts.shipText] - pre-interpolated; computed when omitted
   * @param {object} opts.dateMap
   * @param {object|null} opts.matchedVariant - variant being added to cart
   * @param {object|null} opts.lookup - { colorIdx, sizeIdx }
   * @param {object|null} opts.mainVariant - parent main selection
   * @param {string} opts.parentHandle
   * @param {object} opts.lipKeys
   */
  function buildProperties(opts) {
    if (!opts || !opts.enableProperties) return null;

    var props = {};
    var matched = opts.matchedVariant;
    var lookup = opts.lookup || { colorIdx: -1, sizeIdx: -1 };
    var dateMap = opts.dateMap;
    var lip = opts.lipKeys || {};

    function addProp(key, value) {
      if (typeof key !== 'string' || key.length === 0) return;
      if (value === null || value === undefined) return;
      var s = String(value);
      if (s.length === 0) return;
      props[key] = s;
    }

    var shipText =
      typeof opts.shipText === 'string' && opts.shipText.length > 0
        ? opts.shipText
        : interpolateShipText(
            opts.shipDateTemplate,
            resolveShipDate(dateMap, matched),
          );

    addProp(lip.purchaseType, opts.purchaseTypeLabel);
    addProp(lip.shipDate, shipText);
    addProp(lip.parentHandle, opts.parentHandle);

    if (opts.mainVariant && opts.mainVariant.id) {
      addProp(lip.parentVariantId, String(opts.mainVariant.id));
    }

    if (matched && Array.isArray(matched.options)) {
      if (lookup.colorIdx >= 0) {
        addProp(lip.color, matched.options[lookup.colorIdx]);
      }
      if (lookup.sizeIdx >= 0) {
        addProp(lip.size, matched.options[lookup.sizeIdx]);
      }
    }

    return Object.keys(props).length > 0 ? props : null;
  }

  function dispatchCartAdded(data, addToCartBtn) {
    try {
      document.dispatchEvent(
        new CustomEvent('ajaxProduct:added', {
          detail: {
            product: data,
            addToCartBtn: addToCartBtn || null,
          },
        }),
      );
    } catch (e) {
      console.error(LOG_PREFIX, 'failed to dispatch ajaxProduct:added', e);
    }
    try {
      document.dispatchEvent(new CustomEvent('cart:build'));
    } catch (e) {
      /* noop */
    }
  }

  /**
   * POST /cart/add.js
   *
   * @param {object} payload - { id, quantity, properties? }
   * @param {object} [callbacks] - { addToCartBtn, onSuccess, onError }
   * @returns {Promise<object>}
   */
  function cartAdd(payload, callbacks) {
    callbacks = callbacks || {};
    var body = {
      id: Number(payload.id),
      quantity: payload.quantity != null ? Number(payload.quantity) : 1,
    };
    if (!isFinite(body.quantity) || body.quantity < 1) body.quantity = 1;
    if (payload.properties) body.properties = payload.properties;

    return fetch(getCartAddUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then(function (response) {
        return response.text().then(function (text) {
          var data = null;
          try {
            data = text ? JSON.parse(text) : null;
          } catch (e) {
            /* noop */
          }
          return {
            ok: response.ok,
            status: response.status,
            data: data,
            raw: text,
          };
        });
      })
      .then(function (result) {
        if (!result.ok) {
          var msg =
            (result.data && (result.data.description || result.data.message)) ||
            'Error ' + result.status;
          if (typeof callbacks.onError === 'function') callbacks.onError(msg);
          return result;
        }
        dispatchCartAdded(result.data, callbacks.addToCartBtn);
        if (typeof callbacks.onSuccess === 'function') {
          callbacks.onSuccess(result.data);
        }
        return result;
      })
      .catch(function (err) {
        console.error(LOG_PREFIX, 'fetch failed', err);
        if (typeof callbacks.onError === 'function') {
          callbacks.onError('Network error. Please try again.');
        }
        throw err;
      });
  }

  window.ssc = window.ssc || {};
  window.ssc.MappedPreorderCore = {
    LOG_PREFIX: LOG_PREFIX,
    norm: norm,
    indexOfOption: indexOfOption,
    buildLookup: buildLookup,
    lookupMappedByColorSizeForAvailability:
      lookupMappedByColorSizeForAvailability,
    resolveMapped: resolveMapped,
    resolveMappedFromSelection: resolveMappedFromSelection,
    parseJsonScript: parseJsonScript,
    normalizeDateMap: normalizeDateMap,
    parseDateMap: parseDateMap,
    resolveShipDate: resolveShipDate,
    interpolateShipText: interpolateShipText,
    formatMoney: formatMoney,
    getCartAddUrl: getCartAddUrl,
    findVariantById: findVariantById,
    findMainVariantByColorSize: findMainVariantByColorSize,
    hasPreorderForVariant: hasPreorderForVariant,
    isSizePurchasable: isSizePurchasable,
    updateProductSizePickerAvailability: updateProductSizePickerAvailability,
    getVariantFeaturedSrc: getVariantFeaturedSrc,
    slugifyImageSetName: slugifyImageSetName,
    buildProperties: buildProperties,
    cartAdd: cartAdd,
    dispatchCartAdded: dispatchCartAdded,
  };
})();
